import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import styles from "./Chat.module.scss"
// import {HotelCard} from "./HotelCard";
import { HotelDetailsCard as HotelDetailCard } from "./HotelDetailsCard";
import {RoomCard} from "./RoomCard";
import {Slider} from "./Slider";
import {FlightContainer} from "./FlightContainer";
import {seedMessages} from "../seed";
import type { FilterPhase, FlightFilters, HotelFilters, RoomFilters } from '../types/filterSidebar';
import { filterHotels, filterRooms } from '../utilities/hotelFilters';
import {cancelExecution, executeFlightDetailsPlaybook, executeHotelCardsPlaybook, executeTripIntakePlaybook, loginToGateway, SESSION_TOKEN_KEY} from "../services/api";
import {getFreshIdToken, ReauthenticationRedirectError} from "../services/freshIdToken";
import { DateRangePickerWidget } from './DateRangePickerWidget';
import { PlaceListWidget } from './PlaceListWidget';
import { PartyPickerWidget } from './PartyPickerWidget';
import { FlightContainerWidget } from './FlightContainerWidget';
import { FlightSearchForm } from './FlightSearchForm';
import type { FlightSearchCriteria } from './FlightSearchForm';
import { HotelSearchForm } from './HotelSearchForm';
import { TripSearchForm } from './TripSearchForm';
import type { TripSearchCriteria } from './TripSearchForm';
import type { HotelSearchCriteria } from './HotelSearchForm';
import { FLIGHT_SEED } from '../data/flightSeed';
import { FlightDetailsStep } from './FlightDetailsStep';
import type { FlightDetailsResult } from './FlightDetailsStep';
import { FlightBookingWidget } from './FlightBookingWidget';
import type { FlightBookingResult } from './FlightBookingWidget';
import { Modal } from './Modal';
import type { FlightCardPayload } from './FlightCardWidget';
import type { ChatHistorySummary, SidebarView } from './FlightSearchSummary';
import {AlertTriangle, ArrowRight, LogIn, Mic, Mountain, Palmtree, RefreshCw, Sparkles, UtensilsCrossed} from "lucide-react";
import { KNOWN_CITIES } from '../utilities/cityDetection';
import { useSpeechInput } from '../utilities/useSpeechInput';
import {HotelInfo} from "./HotelInfo";
import {HotelExpanded} from "../unused_components/HotelExpanded";
import {HotelCard} from "../unused_components/HotelCard";

// Types for messages
export type ChatMessage =
    | { id?: string; view?: SidebarView; type: 'text'; text: string; fromUser: boolean }
    | { id?: string; view?: SidebarView; type: 'hotels'; hotels: any[] }
    | { id?: string; view?: SidebarView; type: 'rooms'; rooms: any[]; hotelId?: string; hotelName?: string }
    | { id?: string; view?: SidebarView; type: 'flight'; flights: any[] }
    | { id?: string; view?: SidebarView; executionId?: string; type: 'widget'; envelope: Record<string, unknown> }
    | { id?: string; view?: SidebarView; type: 'error'; text: string; detail?: string }
    | { id?: string; view?: SidebarView; type: 'signin' }
    | { id?: string; view?: SidebarView; type: 'other'; component: React.ReactNode };

type ChatProps = {
    activeView?: SidebarView;
    onSummaryChange?: (summary: ChatHistorySummary) => void;
    onSlotStateChange?: (slotState: Record<string, unknown>) => void;
    onContentTypeChange?: (phase: FilterPhase) => void;
    onStaticDatesChange?: (dates: { checkIn: string; checkOut: string }) => void;
    staticCheckIn?: string;
    staticCheckOut?: string;
    scrollToMessageId?: string;
    onScrollHandled?: () => void;
    initialMessages?: ChatMessage[];
    onMessagesChange?: (messages: ChatMessage[]) => void;
    threadId?: string;
    onThreadIdChange?: (threadId: string) => void;
    /** A message handed over via router state (home page, a destination
     * card, the profile page). Sent once, then cleared by the parent. */
    pendingMessage?: string;
    onPendingMessageHandled?: () => void;
    /** The sidebar's flight filters, applied to every flight list in the thread. */
    flightFilters?: FlightFilters;
    /** The sidebar's hotel and room filters, applied to every hotel / room list in the thread. */
    hotelFilters?: HotelFilters;
    roomFilters?: RoomFilters;
};

const nextId = (prefix: string) => `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
const messageId = (msg: ChatMessage, index: number) => msg.id || `seed-${index}`;

const asRecord = (value: unknown): Record<string, unknown> =>
    value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {};

const firstValue = (...values: unknown[]): unknown => values.find((value) => value !== undefined && value !== null);

const formChildren = (source: Record<string, unknown>): { age: number }[] => {
    const travelers = asRecord(source.travelers);
    const rawChildren = firstValue(travelers.children, source.children);
    const ages = firstValue(source.children_ages, source.childrenAges);
    if (Array.isArray(rawChildren)) {
        return rawChildren.map((child, index) => {
            const value = asRecord(child).age ?? (typeof child === 'number' ? child : undefined) ?? (Array.isArray(ages) ? ages[index] : undefined);
            const age = Number(value);
            return { age: Number.isInteger(age) && age >= 0 && age <= 17 ? age : 8 };
        });
    }
    const count = Number(rawChildren ?? (Array.isArray(ages) ? ages.length : 0));
    return Number.isInteger(count) && count > 0
        ? Array.from({ length: count }, (_, index) => {
            const age = Number(Array.isArray(ages) ? ages[index] : NaN);
            return { age: Number.isInteger(age) && age >= 0 && age <= 17 ? age : 8 };
        })
        : [];
};

const tripSearchEnvelope = (hotelForm: Record<string, unknown>, flightForm: Record<string, unknown>) => {
    const hotelPayload = asRecord(hotelForm.payload);
    const hotel = asRecord(firstValue(hotelPayload.criteria, hotelPayload.data, hotelPayload));
    const flightPayload = asRecord(flightForm.payload);
    const flight = asRecord(firstValue(flightPayload.criteria, flightPayload.data, flightPayload));
    const hotelCheckIn = String(firstValue(hotel.checkIn, hotel.check_in, '') || '');
    const hotelCheckOut = String(firstValue(hotel.checkOut, hotel.check_out, '') || '');
    const departDate = String(firstValue(flight.departDate, flight.depart_date, flight.departure_date, '') || '');
    const returnDate = String(firstValue(flight.returnDate, flight.return_date, '') || '');
    const flightType = String(firstValue(flight.tripType, flight.trip_type, '') || '').toLowerCase();
    const flightTravelers = asRecord(flight.travelers);

    return {
        widget_type: 'trip_search_form',
        payload: {
            hotel: {
                criteria: {
                    destination: String(firstValue(hotel.destination, hotel.city, '') || ''),
                    latitude: hotel.latitude === undefined ? undefined : Number(hotel.latitude),
                    longitude: hotel.longitude === undefined ? undefined : Number(hotel.longitude),
                    radius: hotel.radius === undefined ? undefined : Number(hotel.radius),
                    checkIn: hotelCheckIn,
                    checkOut: hotelCheckOut,
                    rooms: Number(firstValue(hotel.rooms, 1)),
                    adults: Number(firstValue(hotel.adults, flightTravelers.adults, flight.adults, 2)),
                    children: formChildren(hotel),
                },
                defaultDestination: firstValue(hotelPayload.defaultDestination, hotelPayload.default_destination),
            },
            flight: {
                criteria: {
                    tripType: flightType === 'one_way' || flightType === 'one-way' || flightType === 'oneway'
                        ? 'one_way'
                        : flightType === 'round_trip' || flightType === 'round-trip' || returnDate ? 'round_trip' : 'one_way',
                    origin: String(firstValue(flight.origin, flight.from, '') || ''),
                    destination: String(firstValue(flight.destination, flight.to, '') || ''),
                    departDate,
                    ...(returnDate ? { returnDate } : {}),
                    travelers: {
                        adults: Number(firstValue(flightTravelers.adults, flight.adults, hotel.adults, 2)),
                        children: formChildren(flight),
                    },
                },
                defaultOrigin: firstValue(flightPayload.defaultOrigin, flightPayload.default_origin),
                defaultDestination: firstValue(flightPayload.defaultDestination, flightPayload.default_destination),
            },
        },
    };
};

/** Thrown when a request needs a signed-in traveller. The thread answers it
 * with a sign-in card instead of an error. */
class SignInRequiredError extends Error {}

/** The message that met the sign-in card, kept across the Auth0 redirect so
 * the conversation picks up where it stopped. */
const RESUME_MESSAGE_KEY = 'adiona.resumeMessage';

/**
 * Turns a thrown error into something a traveller can act on. The raw text
 * is kept as the detail line — it is usually the gateway's own words, and
 * hiding it makes the failure impossible to report.
 */
const describeError = (raw: string): string => {
    const message = raw.toLowerCase();
    if (message.includes('log in') || message.includes('id token')) return 'You need to sign in first';
    if (message.includes('session')) return "Adiona couldn't start a session";
    if (message.includes('failed to fetch') || message.includes('networkerror')) return "Can't reach Adiona right now";
    if (message.includes('did not complete in time')) return 'That took too long to plan';
    return 'Something went wrong';
};

const failureMessage = (error: any, fallback: string): ChatMessage => {
    if (error instanceof SignInRequiredError) return { id: nextId('assistant'), view: 'searches', type: 'signin' };
    const detail = String(error?.message || fallback);
    return { id: nextId('assistant'), view: 'searches', type: 'error', text: describeError(detail), detail };
};

const HOTEL_CARD_REQUIRED_KEYS = [
    'id', 'name', 'stars', 'bedType', 'price', 'breakfastIncluded', 'image',
    '__renderDetailCard', 'location', 'rating', 'ratingLabel', 'pricePerNight',
    'totalPrice', 'nights', 'amenities', 'description', 'category', 'rooms',
];

const validateHotelCardsShape = (hotels: Record<string, unknown>[]) => {
    return hotels.map((hotel, index) => ({
        index,
        id: String(hotel.id || `hotel-${index + 1}`),
        missing: HOTEL_CARD_REQUIRED_KEYS.filter((key) => !(key in hotel)),
    }));
};


export const Chat=({
    activeView = 'searches',
    onSummaryChange,
    onSlotStateChange,
    onContentTypeChange,
    onStaticDatesChange,
    staticCheckIn,
    staticCheckOut,
    scrollToMessageId,
    onScrollHandled,
    initialMessages,
    onMessagesChange,
    threadId,
    onThreadIdChange,
    pendingMessage,
    onPendingMessageHandled,
    flightFilters,
    hotelFilters,
    roomFilters,
}: ChatProps) => {
    const { isAuthenticated, isLoading: authLoading, getIdTokenClaims, getAccessTokenSilently, loginWithRedirect } = useAuth0();
    const [messages, setMessages] = useState<ChatMessage[]>(() => initialMessages ?? []);
    const [inputValue, setInputValue] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [bookingFlight, setBookingFlight] = useState<FlightCardPayload | null>(null);
    // Step 2 of the demo flight checkout (passenger + payment), shown in the
    // same modal right after FlightDetailsStep's fare/baggage step.
    const [flightBookingStep, setFlightBookingStep] = useState<FlightCardPayload | null>(null);
    const messagesEndRef = useRef<HTMLDivElement | null>(null);
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);
    // Dictation adds to whatever was already typed instead of replacing it,
    // so this holds the field as it was when the mic was switched on.
    const dictationBaseRef = useRef('');
    const speech = useSpeechInput((text) => {
        setInputValue([dictationBaseRef.current.replace(/\s+$/, ''), text].filter(Boolean).join(' '));
    });
    const threadIdRef = useRef(threadId || `chat-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`);
    const activeRequestRef = useRef<AbortController | null>(null);
    const activeExecutionIdRef = useRef<string | undefined>(undefined);
    const messageRefs = useRef(new Map<string, HTMLDivElement>());
    const [highlightedId, setHighlightedId] = useState<string | undefined>(undefined);
    // Last message the user actually sent, so a failed turn's "Try again"
    // can resend it without round-tripping through the composer state.
    const lastUserMessageRef = useRef<string | null>(null);

    const visibleMessages = useMemo(
        () =>
            activeView === 'orders'
                ? messages.filter((message) => message.type === 'widget' && String(message.envelope?.widget_type || '') === 'order_confirmation')
                : messages,
        [activeView, messages]
    );

    const summary = useMemo<ChatHistorySummary>(() => {
        const searches: ChatHistorySummary['searches'] = [];
        const orders: ChatHistorySummary['orders'] = [];
        for (let i = 0; i < messages.length; i += 1) {
            const msg = messages[i];
            if (msg.type !== 'widget') continue;
            const widgetType = String(msg.envelope?.widget_type || '');
            const payload = (msg.envelope?.payload || {}) as Record<string, unknown>;
            const id = messageId(msg, i);
            if (!widgetType) continue;

            if (widgetType === 'order_confirmation') {
                const ref = String(payload.booking_reference || payload.order_id || 'order');
                const currency = String(payload.total_currency || '');
                const total = payload.total_amount === null || payload.total_amount === undefined ? '' : String(payload.total_amount);
                orders.push({ id, label: `Booking ${ref}`, subtitle: `${currency} ${total}`.trim(), widgetType });
            } else if (widgetType === 'flight_list' || widgetType === 'flight_card') {
                const count = Array.isArray(payload.items) ? `${payload.items.length} options` : 'flight';
                searches.push({ id, label: 'Flights', subtitle: count, widgetType });
            } else if (widgetType === 'hotel_list' || widgetType === 'hotel_card') {
                const count = Array.isArray(payload.items) ? `${payload.items.length} stays` : 'hotels';
                searches.push({ id, label: 'Hotels', subtitle: count, widgetType });
            } else if (widgetType === 'place_list' || widgetType === 'place_card') {
                const count = Array.isArray(payload.items) ? `${payload.items.length} places` : 'places';
                searches.push({ id, label: 'Places', subtitle: count, widgetType });
            } else if (widgetType === 'itinerary_summary') {
                searches.push({ id, label: 'Itinerary summary', subtitle: '', widgetType });
            } else if (widgetType === 'calendar_view') {
                searches.push({ id, label: 'Calendar', subtitle: '', widgetType });
            }
        }
        return { searches, orders, threadId: threadIdRef.current };
    }, [messages]);

    // Scroll to bottom on new message
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);
    useEffect(() => {
        onSummaryChange?.(summary);
    }, [onSummaryChange, summary]);
    useEffect(() => {
        onMessagesChange?.(messages);
    }, [onMessagesChange, messages]);
    useEffect(() => {
        if (!scrollToMessageId) return;
        const node = messageRefs.current.get(scrollToMessageId);
        if (node) {
            node.scrollIntoView({ behavior: 'smooth', block: 'center' });
            setHighlightedId(scrollToMessageId);
            const timer = window.setTimeout(() => setHighlightedId(undefined), 1500);
            onScrollHandled?.();
            return () => window.clearTimeout(timer);
        }
        onScrollHandled?.();
        return undefined;
    }, [scrollToMessageId, onScrollHandled]);

    // Sends a message handed over from elsewhere in the app exactly once.
    // The ref (not just the `pendingMessage` prop) guards against sending
    // twice: StrictMode double-invokes this effect on mount, and the
    // parent's prop doesn't clear until its own re-render lands.
    const handledPendingRef = useRef<string | undefined>(undefined);
    useEffect(() => {
        if (!pendingMessage) {
            handledPendingRef.current = undefined;
            return;
        }
        if (handledPendingRef.current === pendingMessage) return;
        handledPendingRef.current = pendingMessage;
        onPendingMessageHandled?.();
        void submitMessage(pendingMessage);
    }, [pendingMessage]);

    // Back from the sign-in redirect: send the message that met the sign-in
    // card. Reading and clearing in one go keeps StrictMode's second run
    // from sending it twice.
    useEffect(() => {
        if (!isAuthenticated) return;
        let resume: string | null = null;
        try {
            resume = sessionStorage.getItem(RESUME_MESSAGE_KEY);
            sessionStorage.removeItem(RESUME_MESSAGE_KEY);
        } catch {
            return;
        }
        if (resume) void submitMessage(resume);
    }, [isAuthenticated]);
    // Messages start empty; content is driven by user input only

    // Auto resize textarea
    const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setInputValue(e.target.value);

        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';  // reset height
            textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';  // grow height
        }
    };

    // The box is disabled while a reply is on its way, and a disabled field
    // loses focus without getting it back when it is enabled again. Hand
    // focus back as soon as the reply (or an error) is shown, so the
    // traveller can keep typing without clicking into the box first.
    const wasSubmittingRef = useRef(false);
    useEffect(() => {
        const finished = wasSubmittingRef.current && !submitting;
        wasSubmittingRef.current = submitting;
        if (!finished) return;
        const active = document.activeElement as HTMLElement | null;
        // Leave it alone if the traveller has moved on to another field (a
        // search form, a date box) or a dialog is open.
        const typingElsewhere = Boolean(
            active && active !== document.body && active !== textareaRef.current
            && active.matches('input, textarea, select, [contenteditable="true"]'),
        );
        if (typingElsewhere || document.querySelector('[data-modal-scroll-container]')) return;
        textareaRef.current?.focus({ preventScroll: true });
    }, [submitting]);

    // Dictated words arrive without a change event, so grow the box for them here.
    useEffect(() => {
        if (!speech.listening || !textareaRef.current) return;
        textareaRef.current.style.height = 'auto';
        textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
    }, [inputValue, speech.listening]);

    const toggleDictation = () => {
        if (!speech.listening) dictationBaseRef.current = inputValue;
        speech.toggle();
    };

    // Handle Enter key to submit
    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            submitMessage();
        }
    };

    // Submit message and clear input. `overrideText` lets a suggestion chip
    // send its own text directly instead of round-tripping through
    // `inputValue` state (which wouldn't be visible to this call yet).
    const submitMessage = async (overrideText?: string) => {
        const text = overrideText ?? inputValue;
        if (!text.trim()) return;

        const userRequest = text.trim();
        lastUserMessageRef.current = userRequest;
        setMessages(prev => [...prev, { id: nextId('user'), view: 'searches', type: 'text', text: userRequest, fromUser: true }]);
        speech.cancel();
        setInputValue('');

        if (textareaRef.current) textareaRef.current.style.height = 'auto';


        // Trip intake asks for missing details, then returns the existing forms
        // with criteria filled in; searching remains the forms' responsibility.
        await handleAiMessage(userRequest);
    };
    const handleRoomsClick = (hotelId: string) => {
        console.log("inside function");
        // Find the hotel message in messages array
        const hotelMessage = messages.find(
            (msg): msg is { type: 'hotels'; hotels: any[] } =>
                msg.type === 'hotels' && msg.hotels.some(h => h.id === hotelId)
        );

        if (!hotelMessage) return;

        // Find the specific hotel and get its rooms
        const hotel = hotelMessage.hotels.find(h => h.id === hotelId);
        if (!hotel || !hotel.rooms || hotel.rooms.length === 0) return;

        // Append a new message of type 'rooms' with these rooms
        console.log("end of function")
        onContentTypeChange?.('rooms');
        setMessages(prev => [...prev, { id: nextId('assistant'), view: 'searches', type: 'rooms', rooms: hotel.rooms, hotelId, hotelName: hotel.name }]);
    };
    const getGatewaySessionToken = async (forceRefresh = false): Promise<string> => {
        const stored = (localStorage.getItem(SESSION_TOKEN_KEY) || '').trim();
        // Once Auth0 has confirmed the traveller is signed out, a leftover
        // session (from before sign-out cleared it, or after the Auth0 session
        // lapsed) must not carry on answering as if they were still in.
        const signedOut = !authLoading && !isAuthenticated;
        if (stored && !forceRefresh && !signedOut) return stored;

        if (!isAuthenticated) {
            throw new SignInRequiredError('Sign in to continue.');
        }

        // Never send an expired cached ID token: refresh it, or re-sign-in.
        let idToken: string;
        try {
            idToken = await getFreshIdToken({ getIdTokenClaims, getAccessTokenSilently, loginWithRedirect });
        } catch (error) {
            if (error instanceof ReauthenticationRedirectError) throw new SignInRequiredError(error.message);
            throw error;
        }

        return loginToGateway(idToken, process.env.REACT_APP_AUTH0_DOMAIN);
    };

    //this code is to test GraphQL using open source country data

    // const handleSearch = async () => {
    //     const data = await client.request<{ country: Country }>(
    //         GET_COUNTRY,
    //         { code: "BR"}
    //     );
    //     setResult(data.country);
    // };

    // console.log(result);
    const handleAiMessage = async (userMessage?: string, turn?: { eventType?: 'user_message' | 'user_widget_submit' | 'user_widget_cta_click'; eventPayload?: Record<string, unknown> }) => {
        const controller = new AbortController();
        activeRequestRef.current?.abort();
        activeRequestRef.current = controller;
        activeExecutionIdRef.current = undefined;
        setSubmitting(true);
        try {
            const runPlanner = async (token: string) =>
                executeTripIntakePlaybook(token, {
                    threadId: threadIdRef.current,
                    userMessage: userMessage ?? '',
                    eventType: turn?.eventType || 'user_message',
                    eventPayload: turn?.eventPayload,
                }, {
                    signal: controller.signal,
                    onExecutionId: (id) => { activeExecutionIdRef.current = id; },
                });

            const result = await (async () => {
                const initialToken = await getGatewaySessionToken();
                try {
                    return await runPlanner(initialToken);
                } catch (firstError: any) {
                    const message = String(firstError?.message || '').toLowerCase();
                    if (!message.includes('session expired')) throw firstError;
                    localStorage.removeItem(SESSION_TOKEN_KEY);
                    const refreshedToken = await getGatewaySessionToken(true);
                    return runPlanner(refreshedToken);
                }
            })();

            if (result.threadAccessDenied) {
                // Not this user's thread (or deleted): continue on a fresh one.
                threadIdRef.current = `chat-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
                onThreadIdChange?.(threadIdRef.current);
            }

            console.log('EXECUTION_START_RAW', result.startResponse);
            console.log('EXECUTION_RESULT_RAW', result.executionResult);
            console.log('FINAL_TRAVEL_DATA', {
                executionId: result.executionId,
                status: result.status,
                forms: result.forms,
                missing: result.missing,
                slotState: result.finalSlotState,
                botMessage: result.botMessage,
                result: (result.executionResult as any)?.result || null,
                data: (result.executionResult as any)?.data || null,
                events: Array.isArray((result.executionResult as any)?.events) ? (result.executionResult as any).events : [],
            });

            setMessages(prev => {
                const next = [...prev];
                const botText = result.botMessage.trim();

                if (botText) {
                    next.push({ id: nextId('assistant'), view: 'searches', type: 'text', text: botText, fromUser: false });
                }
                const hotelForms = result.forms.filter((form) => form.widget_type === 'hotel_search_form');
                const flightForms = result.forms.filter((form) => form.widget_type === 'flight_search_form');
                const canCombineTripForms = hotelForms.length === 1 && flightForms.length === 1;
                let combinedFormAdded = false;
                result.forms.forEach((form) => {
                    if (canCombineTripForms && (form === hotelForms[0] || form === flightForms[0])) {
                        if (!combinedFormAdded) {
                            next.push({
                                id: nextId('assistant'),
                                view: 'searches',
                                executionId: result.executionId,
                                type: 'widget',
                                envelope: tripSearchEnvelope(hotelForms[0], flightForms[0]),
                            });
                            combinedFormAdded = true;
                            onContentTypeChange?.('hotels');
                        }
                        return;
                    }
                    const envelopeWidgetType = String(form.widget_type || '');
                    next.push({
                        id: nextId('assistant'),
                        view: 'searches',
                        executionId: result.executionId,
                        type: 'widget',
                        envelope: form,
                    });
                    if (envelopeWidgetType === 'flight_search_form') {
                        onContentTypeChange?.('flights');
                    } else if (envelopeWidgetType === 'hotel_search_form' || envelopeWidgetType === 'trip_search_form') {
                        onContentTypeChange?.('hotels');
                    }
                });
                return next;
            });

            const finalState = result.finalSlotState;
            onSlotStateChange?.(finalState);
            const checkIn = String(finalState.check_in || finalState.start_date || finalState.depart_date || '');
            const checkOut = String(finalState.check_out || finalState.end_date || finalState.return_date || '');
            if (checkIn || checkOut) {
                onStaticDatesChange?.({
                    checkIn: checkIn || staticCheckIn || '',
                    checkOut: checkOut || staticCheckOut || '',
                });
            }

            // Bank a confirmed booking into the profile so the trip shows up
            // under Profile → Trips. Done outside the state updater because
            // that callback must stay free of side effects.
        } catch (error: any) {
            if (error instanceof DOMException && error.name === 'AbortError') {
                // Request was cancelled, do nothing
            } else {
                setMessages(prev => [...prev, failureMessage(error, 'Failed to run playbook.')]);
            }
        } finally {
            if (activeRequestRef.current === controller) {
                activeRequestRef.current = null;
                activeExecutionIdRef.current = undefined;
                setSubmitting(false);
            }
        }
    };

    const cancelRequest = () => {
        const executionId = activeExecutionIdRef.current;
        if (executionId) {
            void cancelExecution(executionId).catch(() => undefined);
        }
        activeRequestRef.current?.abort();
        activeRequestRef.current = null;
        activeExecutionIdRef.current = undefined;
        setSubmitting(false);
    };

    const handleDateRangeSubmit = async (value: { from: string; to: string; nights: number }) => {
        onStaticDatesChange?.({ checkIn: value.from, checkOut: value.to });
        await handleAiMessage(undefined, {
            eventType: 'user_widget_submit',
            eventPayload: {
                action_id: 'submit_dates',
                value,
                submitted_value: value,
            },
        });
    };

    const handlePlaceSelect = async (value: { label: string; id: string; kind: 'airport' | 'city' | 'landmark' }) => {
        await handleAiMessage(undefined, {
            eventType: 'user_widget_cta_click',
            eventPayload: {
                action_id: 'add_to_itinerary',
                place_id: value.id,
                label: value.label,
                kind: value.kind,
                value,
                submitted_value: value,
            },
        });
    };

    const handlePartySubmit = async (value: { rooms: number; adults: number; children: { age: number }[] }) => {
        await handleAiMessage(undefined, {
            eventType: 'user_widget_submit',
            eventPayload: {
                action_id: 'submit_party',
                value,
                submitted_value: value,
            },
        });
    };

    const handleHotelSearchSubmit = async (criteria: HotelSearchCriteria, formMessageId?: string) => {
        // Remember what was searched on the form's own message, so the
        // selections stay visible in the thread and survive a reload.
        if (formMessageId) {
            setMessages((prev) => prev.map((m) =>
                m.id === formMessageId && m.type === 'widget'
                    ? { ...m, envelope: { ...m.envelope, payload: { ...(m.envelope.payload as Record<string, unknown>), criteria } } }
                    : m
            ));
        }
        onStaticDatesChange?.({ checkIn: criteria.checkIn, checkOut: criteria.checkOut });
        setSubmitting(true);
        try {
            // A destination the form's own suggestions recognise carries its
            // coordinates along too, for a tighter geocode than the bare
            // name alone; a hotel name or an unrecognised place is still
            // sent through as `city` as-is -- the gateway does its own
            // geocoding against whatever string it's given.
            const cityMatch = KNOWN_CITIES.find((city) => city.name.toLowerCase() === criteria.destination.toLowerCase());
            const childCount = criteria.children.length;
            const childAges = criteria.children.map((child) => {
                const rawAge: unknown = child.age;
                const age = typeof rawAge === 'string' ? Number(rawAge.trim()) : Number(rawAge);

                if (
                    (typeof rawAge === 'string' && rawAge.trim() === '') ||
                    !Number.isInteger(age) ||
                    age < 0 ||
                    age > 17
                ) {
                    throw new Error('Please provide a valid age from 0 to 17 for every child.');
                }

                return age;
            });
            const runHotelCards = async (token: string) =>
                executeHotelCardsPlaybook(token, {
                    city: criteria.destination,
                    latitude: criteria.latitude ?? cityMatch?.lat,
                    longitude: criteria.longitude ?? cityMatch?.lng,
                    radius: criteria.radius ?? 20,
                    check_in: criteria.checkIn,
                    check_out: criteria.checkOut,
                    adults: Number(criteria.adults),
                    rooms: Number(criteria.rooms),
                    children: childCount,
                    ...(childCount > 0
                        ? { children_ages: childAges }
                        : {}),
                });

            const hotelResult = await (async () => {
                const initialToken = await getGatewaySessionToken();
                try {
                    return await runHotelCards(initialToken);
                } catch (firstError: any) {
                    const message = String(firstError?.message || '').toLowerCase();
                    if (!message.includes('session expired')) throw firstError;
                    localStorage.removeItem(SESSION_TOKEN_KEY);
                    const refreshedToken = await getGatewaySessionToken(true);
                    return runHotelCards(refreshedToken);
                }
            })();

            const hotels = Array.isArray(hotelResult.hotels)
                ? hotelResult.hotels.map((hotel) => ({ ...hotel, __renderDetailCard: true }))
                : [];

            if (process.env.NODE_ENV !== 'production') {
                console.log('[hotel-cards] raw-result', hotelResult);
                console.log('[hotel-cards] mapped-hotels', hotels);
                console.log('[hotel-cards] shape-check', validateHotelCardsShape(hotels as Record<string, unknown>[]));
            }

            if (hotelResult.check_in && hotelResult.check_out) {
                onStaticDatesChange?.({
                    checkIn: hotelResult.check_in,
                    checkOut: hotelResult.check_out,
                });
            }

            if (!hotels.length) {
                // The playbook came back empty: say so through the normal
                // error message. Nothing stands in for the missing hotels.
                const providerReason = hotelResult.provider_error === null || hotelResult.provider_error === undefined
                    ? ''
                    : String(hotelResult.provider_error);
                const detail = `No hotels were returned from live provider${providerReason ? ` (${providerReason})` : ''} [execution ${hotelResult.executionId}${hotelResult.hotelbeds_env ? `, env ${hotelResult.hotelbeds_env}` : ''}${!providerReason && hotelResult.source ? `, source ${hotelResult.source}` : ''}].`;
                setMessages(prev => [
                    ...prev,
                    { id: nextId('assistant'), view: 'searches', type: 'error', text: describeError(detail), detail },
                ]);
            } else {
                setMessages(prev => [
                    ...prev,
                    {
                        id: nextId('assistant'),
                        view: 'searches' as SidebarView,
                        type: 'hotels' as const,
                        hotels,
                    },
                ]);
            }
        } catch (error: any) {
            setMessages(prev => [...prev, failureMessage(error, 'Failed to load hotels.')]);
        } finally {
            setSubmitting(false);
        }
    };

    const handleFlightSearchSubmit = async (criteria: FlightSearchCriteria, formMessageId?: string) => {
        // Remember what was searched on the form's own message, so the
        // selections stay visible in the thread and survive a reload.
        if (formMessageId) {
            setMessages((prev) => prev.map((m) =>
                m.id === formMessageId && m.type === 'widget'
                    ? { ...m, envelope: { ...m.envelope, payload: { ...(m.envelope.payload as Record<string, unknown>), criteria } } }
                    : m
            ));
        }
        const controller = new AbortController();
        activeRequestRef.current?.abort();
        activeRequestRef.current = controller;
        activeExecutionIdRef.current = undefined;
        setSubmitting(true);
        try {
            const runFlightDetails = async (token: string) => executeFlightDetailsPlaybook(token, criteria, {
                signal: controller.signal,
                onExecutionId: (id) => { activeExecutionIdRef.current = id; },
            });
            const result = await (async () => {
                const initialToken = await getGatewaySessionToken();
                try {
                    return await runFlightDetails(initialToken);
                } catch (firstError: any) {
                    if (!String(firstError?.message || '').toLowerCase().includes('session expired')) throw firstError;
                    localStorage.removeItem(SESSION_TOKEN_KEY);
                    return runFlightDetails(await getGatewaySessionToken(true));
                }
            })();

            const offers = result.offers as FlightCardPayload[];
            const directions = offers.map((offer) => String((offer as any).direction || '').toLowerCase());
            const isRoundTrip = criteria.tripType === 'round_trip' || directions.some((direction) => ['outbound', 'inbound', 'roundtrip', 'round-trip'].includes(direction));
            const outbound = offers.filter((offer) => String((offer as any).direction || '').toLowerCase() === 'outbound');
            const inbound = offers.filter((offer) => String((offer as any).direction || '').toLowerCase() === 'inbound');
            const outboundItems = isRoundTrip && outbound.length ? outbound : offers;
            const hasReturnFlights = inbound.length > 0;
            const payload = {
                title: hasReturnFlights ? 'Outbound flights' : 'Flights',
                items: outboundItems,
                ...(inbound.length ? { return_items: inbound } : {}),
                ...(hasReturnFlights ? { direction: 'roundtrip' } : {}),
                total_count: outboundItems.length,
                currency: result.currency || String((outboundItems[0] as any)?.price?.currency || 'USD'),
            };

            setMessages((prev) => [...prev, {
                id: nextId('assistant'),
                view: 'searches',
                executionId: result.executionId,
                type: 'widget',
                envelope: { widget_type: 'flight_list', payload },
            }]);
            onContentTypeChange?.('flights');
        } catch (error: any) {
            if (!(error instanceof DOMException && error.name === 'AbortError')) {
                setMessages((prev) => [...prev, failureMessage(error, 'Failed to load flights.')]);
            }
        } finally {
            if (activeRequestRef.current === controller) {
                activeRequestRef.current = null;
                activeExecutionIdRef.current = undefined;
                setSubmitting(false);
            }
        }
    };

    /**
     * The combined flights + hotel form: both searches run from one submit,
     * hotels first and flights once the hotel search has finished (whether it
     * found anything or not -- the two searches don't depend on each other).
     * Each reuses the same handler its own form uses, so results, errors and
     * the sidebar behave exactly as they do when the forms are used separately.
     */
    const tripSearchRunningRef = useRef(false);
    const handleTripSearchSubmit = async (criteria: TripSearchCriteria, formMessageId?: string) => {
        if (tripSearchRunningRef.current) return;
        tripSearchRunningRef.current = true;
        try {
            // Remember both searches on the form's own message, so the
            // selections stay visible in the thread and survive a reload.
            if (formMessageId) {
                setMessages((prev) => prev.map((m) => {
                    if (m.id !== formMessageId || m.type !== 'widget') return m;
                    const payload = (m.envelope.payload || {}) as Record<string, any>;
                    return {
                        ...m,
                        envelope: {
                            ...m.envelope,
                            payload: {
                                ...payload,
                                flight: { ...(payload.flight || {}), criteria: criteria.flight },
                                hotel: { ...(payload.hotel || {}), criteria: criteria.hotel },
                            },
                        },
                    };
                }));
            }
            await handleHotelSearchSubmit(criteria.hotel);
            await handleFlightSearchSubmit(criteria.flight);
        } finally {
            tripSearchRunningRef.current = false;
        }
    };

    const handleFlightAction = async (actionId: string, value?: Record<string, unknown>) => {
        // Intercept view_offer to show Flight Details step first
        if (actionId.startsWith('view_offer:')) {
            const offerId = String(value?.offer_id || actionId.slice('view_offer:'.length));
            // Find the flight payload from messages
            const flightPayload = findFlightPayload(offerId);
            if (flightPayload) {
                const outbound = value?.outbound_offer as FlightCardPayload | undefined;
                // price/baggage_options/conditions_options/fare_brand are all
                // offer-level in the API (one set per direction), not
                // itinerary-level, so they have to be copied onto each leg's
                // own itinerary here -- once the two offers are merged into
                // one booking object below, `booking.price` becomes their
                // COMBINED total and only one offer's other top-level fields
                // would otherwise survive at all. FlightDetailsStep reads
                // each leg's own price/baggage/conditions off its itinerary
                // so the outbound and return fare rows price independently.
                const stampLeg = (offer: FlightCardPayload) =>
                    (offer.itineraries || []).map((it) => ({
                        ...it,
                        price: it.price ?? offer.price,
                        baggage_options: it.baggage_options ?? offer.baggage_options,
                        baggage_known: it.baggage_known ?? offer.baggage_known,
                        conditions_options: it.conditions_options ?? offer.conditions_options,
                        conditions_known: it.conditions_known ?? offer.conditions_known,
                        fare_brand: it.fare_brand ?? offer.fare_brand,
                    }));
                if (outbound && outbound.itineraries?.length && flightPayload.itineraries?.length) {
                    const outboundTotal = Number(outbound.price?.total || 0);
                    const returnTotal = Number(flightPayload.price?.total || 0);
                    setBookingFlight({
                        ...flightPayload,
                        offer_id: `${outbound.offer_id}__${flightPayload.offer_id}`,
                        direction: 'round-trip',
                        price: {
                            total: String(outboundTotal + returnTotal),
                            currency: outbound.price?.currency || flightPayload.price?.currency || 'USD',
                        },
                        itineraries: [...stampLeg(outbound), ...stampLeg(flightPayload)],
                        carriers: Array.from(new Set([...(outbound.carriers || []), ...(flightPayload.carriers || [])])),
                        stops: (outbound.stops || 0) + (flightPayload.stops || 0),
                    });
                } else {
                    setBookingFlight({ ...flightPayload, itineraries: stampLeg(flightPayload) });
                }
                return;
            }
        }
        await handleAiMessage(undefined, {
            eventType: 'user_widget_cta_click',
            eventPayload: {
                action_id: actionId,
                value,
                submitted_value: value,
            },
        });
    };

    const findFlightPayload = (offerId: string): FlightCardPayload | null => {
        for (const msg of messages) {
            if (msg.type !== 'widget') continue;
            const wt = String(msg.envelope?.widget_type || '');
            const payload = msg.envelope?.payload as any;
            if (wt === 'flight_list' && Array.isArray(payload?.items)) {
                const allItems = [...payload.items, ...(Array.isArray(payload.return_items) ? payload.return_items : [])];
                const found = allItems.find((item: any) => item.offer_id === offerId);
                if (found) return found as FlightCardPayload;
            }
            if (wt === 'flight_card' && payload?.offer_id === offerId) {
                return payload as FlightCardPayload;
            }
        }
        return null;
    };

    const handleFlightDetailsContinue = (_result: FlightDetailsResult) => {
        const flight = bookingFlight;
        setBookingFlight(null);
        if (!flight) return;
        // Hand off to the passenger/payment step (Step 2 of 5) in the same
        // modal, matching HotelBookingWidget's Reserve → Pay demo checkout —
        // no gateway call happens here.
        setFlightBookingStep(flight);
    };

    const handleFlightBookingContinue = (_result: FlightBookingResult) => {
        // The booking is already banked into the profile inside
        // FlightBookingWidget itself; this just closes the modal.
        setFlightBookingStep(null);
    };

    const retryLast = () => {
        const last = lastUserMessageRef.current;
        if (!last || submitting) return;
        void handleAiMessage(last);
    };

    const signInAndResume = () => {
        const last = lastUserMessageRef.current;
        try {
            if (last) sessionStorage.setItem(RESUME_MESSAGE_KEY, last);
        } catch {
            // Storage can be blocked; signing in still works, the message just isn't resent.
        }
        void loginWithRedirect();
    };

    const isEmptyThread = visibleMessages.length === 0 && !submitting;

    return (
        <div className={`${styles.chatContainer}${isEmptyThread ? ` ${styles.chatContainerEmpty}` : ''}`}>
            {/* The thread is the only thing that scrolls — the sidebars and
                the composer stay put while it moves. */}
            <div className={styles.chatScroll}>
            {isEmptyThread && (
                <div className={styles.emptyState}>
                    <h2 className={styles.emptyHeading}>Where are you headed?</h2>
                    <p className={styles.emptySubtext}>
                        Describe the trip in a sentence. Adiona handles the rest.
                    </p>

                    <div className={styles.categoryGrid}>
                        {[
                            { icon: <UtensilsCrossed size={17} />, label: 'Food & culinary trips', prompt: 'Best food and culinary destinations' },
                            { icon: <Palmtree size={17} />, label: 'Beach escape', prompt: 'Tropical beach escape for a week' },
                            { icon: <Mountain size={17} />, label: 'Adventure & nature', prompt: 'Adventure destination with hiking and nature' },
                            { icon: <Sparkles size={17} />, label: 'Romantic getaway', prompt: 'Romantic getaway for two' },
                        ].map(c => (
                            <button
                                key={c.label}
                                className={styles.categoryCard}
                                type="button"
                                onClick={() => setInputValue(c.prompt)}
                            >
                                <span className={styles.categoryIcon}>{c.icon}</span>
                                <span className={styles.categoryLabel}>{c.label}</span>
                            </button>
                        ))}
                    </div>

                    <div className={styles.suggestedSection}>
                        <span className={styles.suggestedLabel}>Try asking</span>
                        <div className={styles.suggestedChips}>
                            {[
                                'Best destinations for solo travel in Asia',
                                'Flights to London in July, 2 people',
                                'Romantic beach honeymoon under $3,000',
                            ].map(q => (
                                <button
                                    key={q}
                                    className={styles.suggestedChip}
                                    type="button"
                                    onClick={() => void submitMessage(q)}
                                >
                                    {q}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}
            {visibleMessages.map((msg, i) => {
                const id = messageId(msg, i);
                const highlighted = highlightedId === id;
                return (
                    <div
                        key={id}
                        ref={(node) => {
                            if (node) messageRefs.current.set(id, node);
                            else messageRefs.current.delete(id);
                        }}
                        className={`${styles.chatMessage} ${highlighted ? styles.chatMessageHighlighted : ''}`}
                    >
                        {msg.type === 'text' && (
                            <div className={msg.fromUser ? styles.messageBubbleUser : styles.messageBubble}>
                                {msg.text}
                            </div>
                        )}

                    {msg.type === 'signin' && !isAuthenticated && (
                        <div className={styles.signInCard}>
                            <span className={styles.signInIcon}><LogIn size={18} aria-hidden="true" /></span>
                            <div className={styles.signInBody}>
                                <span className={styles.signInTitle}>Sign in to plan this trip</span>
                                <span className={styles.signInText}>
                                    Adiona uses your account to search live flights and hotels and to keep your trips in one place. Once you're in, we'll pick up right where you left off.
                                </span>
                            </div>
                            <button type="button" className={styles.signInButton} onClick={signInAndResume}>
                                Sign in
                            </button>
                        </div>
                    )}

                    {msg.type === 'error' && (
                        <div className={styles.errorNotice} role="alert">
                            <span className={styles.errorIcon}><AlertTriangle size={16} aria-hidden="true" /></span>
                            <div className={styles.errorBody}>
                                <span className={styles.errorTitle}>{msg.text}</span>
                                {msg.detail && <span className={styles.errorDetail}>{msg.detail}</span>}
                            </div>
                            {lastUserMessageRef.current && (
                                <button
                                    type="button"
                                    className={styles.errorRetry}
                                    onClick={retryLast}
                                    disabled={submitting}
                                >
                                    <RefreshCw size={13} aria-hidden="true" /> Try again
                                </button>
                            )}
                        </div>
                    )}

                    {msg.type === 'hotels' && (() => {
                        const shown = filterHotels(
                            msg.hotels.filter((hotel) => hotel.__renderDetailCard === true),
                            hotelFilters,
                        );
                        return shown.length === 0 && msg.hotels.length > 0 ? (
                            <p className={styles.noFilterMatches}>No hotels match the current filters. Adjust them in the sidebar.</p>
                        ) : (
                            <Slider slidesToShow={3}>
                                {shown.map((hotel) => (
                                    <HotelDetailCard key={hotel.id} hotel={hotel} onViewRooms={(h) => handleRoomsClick(String(h?.id || ''))} />
                                ))}
                            </Slider>
                        );
                    })()}

                    {msg.type === 'rooms' && msg.hotelId && msg.rooms && (() => {
                        const shown = filterRooms(msg.rooms, roomFilters);
                        return shown.length === 0 && msg.rooms.length > 0 ? (
                            <p className={styles.noFilterMatches}>No rooms match the current filters. Adjust them in the sidebar.</p>
                        ) : (
                            <Slider slidesToShow={3}>
                                {shown.map(room => (
                                    <RoomCard
                                        key={room.id}
                                        room={room}
                                        staticCheckIn={staticCheckIn}
                                        staticCheckOut={staticCheckOut}
                                    />
                                ))}
                            </Slider>
                        );
                    })()}

                    {msg.type === 'flight' && (
                        <div><FlightContainer flights={msg.flights} /></div>
                    )}

                    {msg.type === 'other' && (
                        <>{msg.component}</>
                    )}

                    {msg.type === 'widget' && String(msg.envelope?.widget_type || '') === 'flight_search_form' && (
                        <FlightSearchForm
                            initialCriteria={(msg.envelope?.payload as { criteria?: FlightSearchCriteria } | undefined)?.criteria}
                            defaultDestination={(msg.envelope?.payload as { defaultDestination?: string } | undefined)?.defaultDestination}
                            onSearch={(criteria) => handleFlightSearchSubmit(criteria, msg.id)}
                        />
                    )}

                    {msg.type === 'widget' && String(msg.envelope?.widget_type || '') === 'trip_search_form' && (() => {
                        // The planner sends the two data sets side by side, each in
                        // the same shape its own form's envelope uses.
                        const payload = (msg.envelope?.payload || {}) as {
                            flight?: { criteria?: FlightSearchCriteria; defaultOrigin?: string; defaultDestination?: string };
                            hotel?: { criteria?: HotelSearchCriteria; defaultDestination?: string };
                        };
                        return (
                            <TripSearchForm
                                initialFlight={payload.flight?.criteria}
                                initialHotel={payload.hotel?.criteria}
                                defaultOrigin={payload.flight?.defaultOrigin}
                                defaultDestination={payload.flight?.defaultDestination}
                                defaultHotelDestination={payload.hotel?.defaultDestination}
                                onSearch={(criteria) => handleTripSearchSubmit(criteria, msg.id)}
                            />
                        );
                    })()}

                    {msg.type === 'widget' && String(msg.envelope?.widget_type || '') === 'hotel_search_form' && (
                        <HotelSearchForm
                            initialCriteria={(msg.envelope?.payload as { criteria?: HotelSearchCriteria } | undefined)?.criteria}
                            defaultDestination={(msg.envelope?.payload as { defaultDestination?: string } | undefined)?.defaultDestination}
                            onSearch={(criteria) => handleHotelSearchSubmit(criteria, msg.id)}
                        />
                    )}

                    {msg.type === 'widget' && String(msg.envelope?.widget_type || '') === 'date_range_picker' && (
                        <DateRangePickerWidget
                            payload={(msg.envelope?.payload || {}) as any}
                            onSubmit={handleDateRangeSubmit}
                        />
                    )}

                    {msg.type === 'widget' && ['place_list', 'place_card'].includes(String(msg.envelope?.widget_type || '')) && (
                        <PlaceListWidget
                            payload={(String(msg.envelope?.widget_type || '') === 'place_card'
                            ? {
                                title: 'Place',
                                items: [((msg.envelope?.payload || {}) as Record<string, unknown>)],
                            }
                            : (msg.envelope?.payload || { title: 'Places', items: [] })) as any}
                            onAddPlace={handlePlaceSelect}
                        />
                    )}

                    {msg.type === 'widget' && String(msg.envelope?.widget_type || '') === 'party_picker' && (
                        <PartyPickerWidget
                            payload={(msg.envelope?.payload || { rooms_max: 9, adults_max: 9, children_max: 6, allow_child_ages: false }) as any}
                            onSubmit={handlePartySubmit}
                        />
                    )}

                    {msg.type === 'widget' && String(msg.envelope?.widget_type || '') === 'flight_list' && (
                        <FlightContainerWidget
                            payload={(msg.envelope?.payload || { title: 'Flights', items: [], total_count: 0, currency: 'USD' }) as any}
                            onAction={handleFlightAction}
                            filters={flightFilters}
                        />
                    )}

                    {msg.type === 'widget' && String(msg.envelope?.widget_type || '') === 'flight_card' && (
                        <FlightContainerWidget
                            payload={{
                                title: 'Flight offer',
                                items: [(msg.envelope?.payload || {}) as any],
                                total_count: 1,
                                currency: String((msg.envelope?.payload as any)?.price?.currency || 'USD'),
                            }}
                            onAction={handleFlightAction}
                            filters={flightFilters}
                        />
                    )}
                    {msg.type === 'widget' && msg.executionId ? (
                        <div className={styles.executionId}>Execution {msg.executionId}</div>
                    ) : null}
                </div>
            )})}
            {/* Was inline as the next "message" in the thread — now a modal,
                matching how HotelDetailsCard and DestinationExpanded open
                their own detail views. Step 1 (fare/baggage) hands off to
                step 2 (passenger/payment) in the same modal. */}
            <Modal
                isOpen={Boolean(bookingFlight) || Boolean(flightBookingStep)}
                onClose={() => { setBookingFlight(null); setFlightBookingStep(null); }}
                size="pageNarrow"
            >
                {bookingFlight && (
                    <FlightDetailsStep
                        booking={bookingFlight}
                        onContinue={handleFlightDetailsContinue}
                    />
                )}
                {flightBookingStep && (
                    <FlightBookingWidget
                        booking={flightBookingStep}
                        onContinue={handleFlightBookingContinue}
                    />
                )}
            </Modal>
            <div ref={messagesEndRef} />

            {submitting && (
                <div className={styles.planningIndicator}>
                    <span className={styles.spinner} />
                    <span className={styles.planningText}>Adiona is planning</span>
                    <button className={styles.cancelButton} onClick={cancelRequest}>
                        Cancel
                    </button>
                </div>
            )}

            {/*this button triggers function and displays results of testing GraphQL using opensource country data.*/}

            {/*<button*/}
            {/*    onClick={handleSearch}*/}
            {/*>GrapgQL</button>*/}
            {/*/!* {sseClientId && (*/}
            {/*    <div className={styles.messageBubble}>SSE Client ID: {sseClientId}</div>*/}
            {/*)} *!/*/}
            {/*<Slider slidesToShow={3}>*/}
            {/*    {result && (*/}
            {/*        <DestinationCard destination={result} />*/}
            {/*    )}*/}
            {/*    {result && (*/}
            {/*        <DestinationCard destination={result} />*/}
            {/*    )}*/}
            {/*</Slider>*/}

            </div>
            {/* AI responses are appended in the main chat message list above */}
            {/* Search bar */}
            <div className={styles.chatInputWrapper}>
                <form
                    className={styles.inputAIForm}
                    onSubmit={(e) => {
                        e.preventDefault();
                        void submitMessage();
                    }}
                >
                    <textarea id="aiInput"
                              ref={textareaRef}
                              placeholder={speech.listening ? "Listening…" : "Describe your travel plans..."}
                              className={styles.textareaInputAI}
                              value={inputValue}
                              disabled={submitting}
                              onChange={handleInputChange}
                              onKeyDown={handleKeyDown}
                              rows={1}
                              style={{ overflow: 'hidden' }}
                    />
                    {speech.supported && (
                        <button
                            type="button"
                            className={`${styles.micBtn}${speech.listening ? ` ${styles.micBtnListening}` : ''}`}
                            onClick={toggleDictation}
                            disabled={submitting}
                            aria-pressed={speech.listening}
                            aria-label={speech.listening ? "Stop listening" : "Say your message out loud"}
                            title={speech.blocked ? "Microphone is blocked. Allow it in your browser's address bar to talk." : undefined}
                        >
                            <Mic size={17} aria-hidden="true" />
                        </button>
                    )}
                    <button id="submitBtn"
                            type="submit"
                            className={styles.sendBtn}
                            disabled={submitting || !inputValue.trim()}
                            title="Submit Button">
                        <ArrowRight size={18} strokeWidth={2.5} />
                    </button>
                </form>
                <div className={styles.chatInputHint}>
                    <span>Press `Enter` to send</span>
                    <span>Press `Shift + Enter` for a new line</span>
                </div>
            </div>
        </div>
    );
}
